/**
 * Collection meta-progress (v0.61): the title-screen reference lists show
 * everything, but entries render GRAYED OUT until the player has actually
 * collected them in a run — relics owned, rewards chosen, bosses fought,
 * creatures recruited, passives awakened. Rarity colors stay visible.
 * Lives in localStorage, independent of run saves.
 */
export type CollectionKind = 'relics' | 'rewards' | 'bosses' | 'creatures' | 'passives' | 'skills' | 'enemies' | 'emods'

const STORAGE_KEY = 'elemental-dispatch-collection'
/** ✨ v3.49: which entries the player has actually OPENED in the codex. */
const VIEWED_KEY = 'elemental-dispatch-collection-viewed'
/** 🏆 v6.03: the third tier - owned / defeated / used. */
const MASTERED_KEY = 'elemental-dispatch-collection-mastered'

type CollectionState = Record<CollectionKind, string[]>

// 👹 'enemies' since v2.32 (the Collection screen) — recorded when a
// mission with painted enemy art spawns on the field.
const EMPTY: CollectionState = { relics: [], rewards: [], bosses: [], creatures: [], passives: [], skills: [], enemies: [], emods: [] }

export function useCollection() {
  const collection = useState<CollectionState>('collection', () => structuredClone(EMPTY))
  const mastered = useState<CollectionState>('collection-mastered', () => structuredClone(EMPTY))
  const viewed = useState<string[]>('collection-viewed', () => [])
  const loaded = useState('collection-loaded', () => false)

  /** Reads storage exactly once per session — record() must never write
   * before this has run, or it would clobber the stored codex. */
  function load() {
    if (loaded.value || typeof localStorage === 'undefined') return
    loaded.value = true
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Partial<CollectionState>
      collection.value = {
        ...structuredClone(EMPTY),
        ...Object.fromEntries(
          Object.entries(raw).filter(([, v]) => Array.isArray(v)),
        ),
      }
      const rawM = JSON.parse(localStorage.getItem(MASTERED_KEY) ?? '{}') as Partial<CollectionState>
      mastered.value = {
        ...structuredClone(EMPTY),
        ...Object.fromEntries(
          Object.entries(rawM).filter(([, v]) => Array.isArray(v)),
        ),
      }
    } catch { /* corrupt entry: start fresh */ }
    loadViewed()
  }

  /**
   * ✨ v3.49 (per Javi): a found-but-never-opened entry wears a spinning
   * glow in the Collection until the player taps it. That means the FIRST
   * read on an existing save has to seed every already-known entry as
   * viewed — otherwise a veteran's codex would light up wall to wall.
   */
  function loadViewed() {
    const raw = localStorage.getItem(VIEWED_KEY)
    if (raw === null) {
      viewed.value = everything()
      persistViewed()
      return
    }
    try {
      const parsed = JSON.parse(raw) as unknown
      viewed.value = Array.isArray(parsed) ? parsed.filter(v => typeof v === 'string') : []
    } catch { viewed.value = [] }
  }

  /** Every recorded entry as a `kind:id` key. */
  function everything(): string[] {
    return (Object.keys(EMPTY) as CollectionKind[])
      .flatMap(kind => collection.value[kind].map(id => `${kind}:${id}`))
  }

  function persist() {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(collection.value))
    }
  }

  function persistViewed() {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(VIEWED_KEY, JSON.stringify(viewed.value))
    }
  }

  /** Found since the player last looked — the glow condition. */
  function isNew(kind: CollectionKind, id: string): boolean {
    return collection.value[kind].includes(id) && !viewed.value.includes(`${kind}:${id}`)
  }

  function markViewed(kind: CollectionKind, id: string) {
    load()
    const key = `${kind}:${id}`
    if (viewed.value.includes(key)) return
    viewed.value = [...viewed.value, key]
    persistViewed()
  }

  /**
   * Adds an entry to the codex. `announce: false` records it QUIETLY —
   * counted as found, but pre-marked viewed so it never wears the ✨ New
   * glow. v3.97 (per Javi: "only make it say new when the player OBTAINS
   * a new creature, not just for simply playing with it through the
   * draft"): drafting and mid-run recruiting are play, not acquisition —
   * only the shop and booster flows announce.
   */
  function record(kind: CollectionKind, id: string | null | undefined, opts?: { announce?: boolean }) {
    if (!id) return
    load()
    if (!collection.value[kind].includes(id)) {
      collection.value[kind].push(id)
      if (opts?.announce === false) {
        const key = `${kind}:${id}`
        if (!viewed.value.includes(key)) {
          viewed.value = [...viewed.value, key]
          persistViewed()
        }
      }
      persist()
    }
  }

  function has(kind: CollectionKind, id: string): boolean {
    load()
    return collection.value[kind].includes(id)
  }

  function persistMastered() {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(MASTERED_KEY, JSON.stringify(mastered.value))
    }
  }

  /** 🏆 v6.03 (per Javi's 3-state codex): the third tier - owned,
   * defeated, used. Mastering implies the sighting. */
  function master(kind: CollectionKind, id: string | null | undefined, opts?: { announce?: boolean }) {
    if (!id) return
    record(kind, id, { announce: false, ...opts })
    if (!mastered.value[kind].includes(id)) {
      mastered.value[kind].push(id)
      persistMastered()
    }
  }

  function hasMastered(kind: CollectionKind, id: string): boolean {
    load()
    return mastered.value[kind].includes(id)
  }

  /** ☁️ Cloud sync: union the remote mastered lists in. */
  function adoptMastered(remote: Partial<CollectionState>) {
    load()
    let changed = false
    for (const kind of Object.keys(EMPTY) as CollectionKind[]) {
      const incoming = remote[kind]
      if (!Array.isArray(incoming)) continue
      const merged = [...new Set([...mastered.value[kind], ...incoming.filter(v => typeof v === 'string')])]
      if (merged.length !== mastered.value[kind].length) {
        mastered.value[kind] = merged
        changed = true
      }
    }
    if (changed) persistMastered()
  }

  function count(kind: CollectionKind): number {
    return collection.value[kind].length
  }

  /** ☁️ Cloud sync (v1.17): union each codex list with the remote copy. */
  function adopt(remote: Partial<CollectionState>) {
    load()
    let changed = false
    for (const kind of Object.keys(EMPTY) as CollectionKind[]) {
      const incoming = remote[kind]
      if (!Array.isArray(incoming)) continue
      const merged = [...new Set([...collection.value[kind], ...incoming.filter(v => typeof v === 'string')])]
      if (merged.length !== collection.value[kind].length) {
        collection.value[kind] = merged
        changed = true
      }
    }
    if (changed) {
      persist()
      // ✨ v3.49: entries that arrive from ANOTHER device were unlocked
      // somewhere the player was already looking — glowing them here
      // would light up half the codex on every new phone.
      viewed.value = [...new Set([...viewed.value, ...everything()])]
      persistViewed()
    }
  }

  return { collection, mastered, viewed, load, record, master, has, hasMastered, count, adopt, adoptMastered, isNew, markViewed }
}
